<template>
  <div>
    <v-dialog v-model="dialog" eager persistent scrollable max-width="680">
      <v-form ref="UserForm" @submit.prevent>
        <v-card class="rounded-2xl dialog-card">
          <!-- Dialog Header -->
          <div
            class="dialog-header px-6 py-5 d-flex align-center justify-space-between"
          >
            <div class="d-flex align-center" style="gap: 12px">
              <div class="dialog-icon-wrap">
                <v-icon color="#00A63E" size="18">
                  {{
                    action === "Add"
                      ? "mdi-account-plus-outline"
                      : "mdi-account-edit-outline"
                  }}
                </v-icon>
              </div>
              <div>
                <div class="text-white">{{ action }} User</div>
                <div class="text-white">
                  {{
                    action === "Add"
                      ? "Fill in the details to create a new user."
                      : "Update the user information below."
                  }}
                </div>
              </div>
            </div>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              class="close-btn"
              @click="closeD()"
            />
          </div>

          <v-divider />

          <!-- Dialog Body -->
          <v-card-text class="px-6 py-5">
            <v-row dense>
              <!-- Full name (user_type == 7) -->
              <v-col cols="12" v-if="user_type == 7">
                <label class="field-label">Full Name</label>
                <v-text-field
                  v-model="name"
                  placeholder="Enter full name"
                  :rules="[formRules.required]"
                  variant="outlined"
                  density="compact"
                  class="mt-1 rounded-lg"
                  :color="$vuetify.theme.themes.light.inputColor"
                />
              </v-col>

              <!-- Name fields -->
              <v-col cols="4" v-if="user_type != 7">
                <label class="field-label"
                  >First Name <span class="req">*</span></label
                >
                <v-text-field
                  v-model="fname"
                  placeholder="First name"
                  :rules="[formRules.required]"
                  variant="outlined"
                  density="compact"
                  class="mt-1 rounded-lg"
                  :color="$vuetify.theme.themes.light.inputColor"
                />
              </v-col>
              <v-col cols="4" v-if="user_type != 7">
                <label class="field-label">Middle Name</label>
                <v-text-field
                  v-model="mname"
                  placeholder="Middle name"
                  variant="outlined"
                  density="compact"
                  class="mt-1 rounded-lg"
                  :color="$vuetify.theme.themes.light.inputColor"
                />
              </v-col>
              <v-col cols="4" v-if="user_type != 7">
                <label class="field-label"
                  >Last Name <span class="req">*</span></label
                >
                <v-text-field
                  v-model="lname"
                  placeholder="Last name"
                  :rules="[formRules.required]"
                  variant="outlined"
                  density="compact"
                  class="mt-1 rounded-lg"
                  :color="$vuetify.theme.themes.light.inputColor"
                />
              </v-col>
              <v-col cols="4" v-if="user_type != 7">
                <label class="field-label">Suffix</label>
                <v-text-field
                  v-model="suffix"
                  placeholder="e.g. Jr., III"
                  variant="outlined"
                  density="compact"
                  class="mt-1 rounded-lg"
                  :color="$vuetify.theme.themes.light.inputColor"
                />
              </v-col>

              <!-- Email & Password (Add only) -->
              <v-col cols="4" v-if="action == 'Add'">
                <label class="field-label"
                  >Email <span class="req">*</span></label
                >
                <v-text-field
                  v-model="email"
                  placeholder="email@example.com"
                  :rules="[formRules.required, formRules.email]"
                  variant="outlined"
                  density="compact"
                  class="mt-1 rounded-lg"
                  :color="$vuetify.theme.themes.light.inputColor"
                />
              </v-col>
              <v-col cols="4" v-if="action == 'Add'">
                <label class="field-label"
                  >Password <span class="req">*</span></label
                >
                <v-text-field
                  v-model="pass"
                  :rules="[formRules.required, formRules.password]"
                  placeholder="Password"
                  :color="$vuetify.theme.themes.light.inputColor"
                  :type="showPassword ? 'text' : 'password'"
                  :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append-inner="showPassword = !showPassword"
                  variant="outlined"
                  density="compact"
                  class="mt-1 rounded-lg"
                />
              </v-col>

              <!-- Institute -->
              <v-col cols="12" v-if="user_type != 7 && user_type != 8">
                <label class="field-label"
                  >Institute <span class="req">*</span></label
                >
                <v-autocomplete
                  v-model="selected_institute"
                  :rules="[formRules.required]"
                  :items="instituteList"
                  item-value="id"
                  item-title="name"
                  placeholder="Select institute"
                  variant="outlined"
                  density="compact"
                  class="mt-1 rounded-lg"
                  :color="$vuetify.theme.themes.light.inputColor"
                />
              </v-col>

              <!-- User Type (Add only) -->
              <v-col cols="12" v-if="action == 'Add'">
                <label class="field-label"
                  >User Type <span class="req">*</span></label
                >
                <v-autocomplete
                  v-model="user_type"
                  :rules="[formRules.required]"
                  :items="usertypeList"
                  item-value="id"
                  item-title="description"
                  placeholder="Select user type"
                  variant="outlined"
                  density="compact"
                  class="mt-1 rounded-lg"
                  :color="$vuetify.theme.themes.light.inputColor"
                />
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider />

          <!-- Dialog Actions -->
          <v-card-actions
            class="px-6 py-4 d-flex justify-end"
            style="gap: 10px"
          >
            <v-btn
              variant="text"
              class="cancel-btn"
              rounded="lg"
              @click="closeD()"
            >
              Cancel
            </v-btn>
            <v-btn
              class="save-btn"
              rounded="lg"
              :disabled="btnLoading"
              :loading="btnLoading"
              @click="action == 'Add' ? save() : update()"
            >
              <v-icon size="16" class="mr-1">mdi-check-circle-outline</v-icon>
              Save Changes
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <SkillAssessmentBatchDialog
      :data="batch_group"
      :action="'Add'"
      :batchToAdd="2"
    />

    <v-dialog v-model="dialogConfirmDelete" max-width="460">
      <v-card class="rounded-2xl pa-2">
        <v-card-title class="d-flex align-center pt-4 px-4" style="gap: 10px">
          <div class="delete-icon-wrap">
            <v-icon color="#ef4444" size="20">mdi-alert-outline</v-icon>
          </div>
          <span style="font-size: 16px; font-weight: 700; color: #111827"
            >Remove Student</span
          >
        </v-card-title>
        <v-card-text class="px-4 py-3" style="font-size: 14px; color: #6b7280">
          Are you sure you want to remove this student from the list?
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn
            variant="text"
            class="cancel-btn"
            rounded="lg"
            @click="dialogConfirmDelete = false"
          >
            Cancel
          </v-btn>
          <v-btn class="confirm-delete-btn" rounded="lg" @click="proceedDelete">
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </div>
</template>

<script>
import eventBus from "@/eventBus";
import SkillAssessmentBatchDialog from "./SkillAssessment/SkillAssessmentBatchDialog.vue";

export default {
  props: {
    data: Object,
    action: String,
  },

  components: {
    SkillAssessmentBatchDialog,
  },

  data() {
    return {
      dialogConfirmDelete: false,
      showPassword: false,
      dialog: false,
      name: null,
      fname: null,
      mname: null,
      lname: null,
      suffix: null,
      pass: null,
      program_id: [],
      email: null,
      address: null,
      batch_selected: null,
      batch: null,
      batch_year: null,
      batch_group: null,
      batchList: [],
      program_tied_id: null,
      setName: null,
      btnLoading: false,
      loading: false,
      usertypeList: [],
      userroleList: [],
      user_type: null,
      user_role: [],
      programsList: [],
      institute_id: null,
      instituteList: [],
      selected_institute: null,
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "Successfully Added!",
        message: "",
        top: 10,
      },
      fetched_data: [],
      removedStudent: [],
    };
  },

  watch: {
    data: {
      handler(data) {
        this.dialog = true;
        this.initialize();
        if (data.id) {
          this.name = data.userdetail.name;
          this.fname = data.userdetail.fname;
          this.mname = data.userdetail.mname;
          this.lname = data.userdetail.lname;
          this.suffix = data.userdetail.suffix;
          this.user_type = data.usertypeID;
          this.user_role = data.user_roleID;
          this.selected_institute = data.instituteID;

          let progs =
            data.usertypeID == 3
              ? data.programs_handled
              : data.usertypeID == 7
              ? data.userdetail.program_tied_id
              : null;

          if (progs) {
            for (let i = 0; i < progs.length; i++) {
              progs[i] = progs[i].toString();
            }
          }
          this.program_id = progs;
        } else {
          this.selected_institute = null;
          this.user_type = null;
          this.$refs.UserForm.reset();
        }
        this.$refs.UserForm.resetValidation();
      },
      deep: true,
    },

    parsed: {
      handler(data) {
        if (data) this.fetchStudents();
      },
    },

    selected_institute: {
      handler(data) {
        if (data) this.getAllProgramsByInstitute();
      },
    },

    program_id: {
      handler(data) {
        if (data) this.fetchStudents();
      },
    },
  },

  mounted() {
    eventBus.on("closeSkillAssessmentBatchDialog", () => {
      this.getBatchList();
    });
  },

  beforeUnmount() {
    eventBus.off("closeSkillAssessmentBatchDialog");
  },

  methods: {
    initialize() {
      this.getAllInstitutes();
      this.getAllUserTypes();
      this.getAllUserRoles();
    },

    fetchStudents() {
      if (this.batch_selected && this.program_id && this.csv_results?.data) {
        let data = {
          students: JSON.stringify(this.csv_results.data),
          batch_id: this.batch_selected,
          program_id: this.program_id,
        };
        this.axiosCall("/student/check-students", "POST", data).then((res) => {
          if (res.data.status == 200) {
            this.fetched_data = res.data.data;
          }
        });
      }
    },

    loadClasslist() {
      this.axiosCall("/student/getMyStudentBySet/" + this.data.id, "GET").then(
        (res) => {
          if (res) {
            this.parsed = true;
            this.fetched_data = res.data;
          }
        },
      );
    },

    showAddBatch() {
      if (this.batch_selected == -1) {
        this.batch_group = { id: null };
      }
    },

    remove(item, index) {
      this.itemToDelete = item;
      this.indexToDelete = index;
      this.dialogConfirmDelete = true;
    },

    proceedDelete() {
      this.removedStudent.push(this.itemToDelete);
      this.fetched_data.splice(this.indexToDelete, 1);
      this.dialogConfirmDelete = false;
      this.fadeAwayMessage.show = true;
      this.fadeAwayMessage.type = "success";
      this.fadeAwayMessage.message = "Removed successfully";
      this.fadeAwayMessage.header = "System Message";
      this.itemToDelete = null;
      this.indexToDelete = null;
    },

    async save() {
      let validation = await this.$refs.UserForm.validate();
      if (validation.valid) {
        this.btnLoading = true;
        let prog = [];
        if (typeof this.program_id == "object" && this.user_type == 3) {
          for (let i = 0; i < this.program_id.length; i++) {
            prog.push(parseInt(this.program_id[i]));
          }
        }
        let data = {
          name: this.name,
          fname: this.fname,
          mname: this.mname,
          lname: this.lname,
          suffix: this.suffix,
          email: this.email,
          password: this.pass,
          institute_id: this.selected_institute,
          usertype_id: this.user_type,
        };
        this.axiosCall("/auth/add-user", "POST", data).then((res) => {
          if (res.data.status == 201) {
            this.btnLoading = false;
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.message = "Added successfully";
            this.fadeAwayMessage.header = "System Message";
            this.closeD();
          } else {
            this.btnLoading = false;
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.message = res.data.msg;
            this.fadeAwayMessage.header = "System Message";
          }
        });
      }
    },

    async update() {
      let validation = await this.$refs.UserForm.validate();
      if (validation.valid) {
        this.btnLoading = true;
        let data = {
          userdetailID: this.data.userdetail.id,
          userID: this.data.id,
          name: this.name,
          fname: this.fname,
          mname: this.mname,
          lname: this.lname,
          suffix: this.suffix,
          usertype_id: this.user_type,
        };
        this.axiosCall("/auth/update-user-detail", "POST", data).then((res) => {
          if (res.data.status == 200) {
            this.btnLoading = false;
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.message = res.data.msg;
            this.fadeAwayMessage.header = "System Message";
            this.closeD();
          } else {
            this.btnLoading = false;
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.message = res.data.msg;
            this.fadeAwayMessage.header = "System Message";
          }
        });
      }
    },

    closeD() {
      eventBus.emit("closeUserManagementDialog", true);
      this.dialog = false;
    },

    getBatchList() {
      this.axiosCall("/student/get-batch-list", "GET").then((res) => {
        this.batchList = res.data;
      });
    },

    getAllProgramsByInstitute() {
      this.axiosCall(
        "/program/getAllProgramsByInstitute/" + this.selected_institute,
        "GET",
      ).then((res) => {
        this.programsList = res.data;
      });
    },

    getAllInstitutes() {
      this.axiosCall("/institutes", "GET").then((res) => {
        this.instituteList = res.data;
      });
    },

    getAllUserTypes() {
      if (this.action == "Add") {
        this.usertypeList = [
          { id: 3, description: "OJT Coordinator" },
          { id: 4, description: "Instructor" },
          { id: 5, description: "Dean" },
        ];
      } else {
        this.axiosCall("/user-type/getAllUsertype", "GET").then((res) => {
          this.usertypeList = res.data;
        });
      }
    },

    getAllUserRoles() {
      this.axiosCall("/user-role", "GET").then((res) => {
        this.userroleList = res.data;
      });
    },
  },
};
</script>

<style scoped>
.dialog-card {
  border: 1px solid #e5e7eb !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
  font-family: "DM Sans", "Nunito", sans-serif;
}

/* ─── Dialog header ─── */
.dialog-header {
  background: #fafafa;
}
.dialog-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.dialog-title {
  font-size: 16px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.2px;
}
.dialog-subtitle {
  font-size: 12.5px;
  color: #6b7280;
  margin-top: 2px;
}
.close-btn {
  color: #9ca3af !important;
}

/* ─── Field labels ─── */
.field-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #374151;
  display: block;
}
.req {
  color: #ef4444;
  margin-left: 2px;
}

/* ─── Buttons ─── */
.cancel-btn {
  font-weight: 600 !important;
  color: #6b7280 !important;
  font-size: 13px !important;
}
.save-btn {
  background: #00a63e !important;
  color: #fff !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  box-shadow: 0 2px 8px rgba(0, 166, 62, 0.25) !important;
}
.save-btn:hover {
  background: #008f34 !important;
}

/* ─── Delete dialog ─── */
.delete-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  display: flex;
  align-items: center;
  justify-content: center;
}
.confirm-delete-btn {
  background: #ef4444 !important;
  color: #fff !important;
  font-weight: 600 !important;
  font-size: 13px !important;
}
</style>
